const dayMap = {
  sunday: "SU",
  monday: "MO",
  tuesday: "TU",
  wednesday: "WE",
  thursday: "TH",
  friday: "FR",
  saturday: "SA",
};

const getBYDAY = (days = []) =>
  days.map((d) => dayMap[d.toLowerCase()]).join(",");

const getDayIndex = (day) =>
  [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ].indexOf(day.toLowerCase());

export const formatDate = (date) =>
  new Date(date).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

// ============================================================
// DECISION LOGIC — can this recurrence become ONE valid RRULE?
// ============================================================

const isValidDailyRRULE = (recurrence) =>
  recurrence?.period === "day" || recurrence?.period == null;

const isValidWeeklyRRULE = (recurrence) =>
  recurrence?.period === "week" && recurrence?.weekDaySelected?.length > 0;

const isValidMonthlyRRULE = (recurrence) =>
  recurrence?.period === "month" && recurrence?.monthDaySelected?.length === 1;

const isValidYearlyRRULE = (recurrence) => recurrence?.period === "year";

const canBuildRRULE = (recurrence) => {
  if (!recurrence) return false;
  return (
    isValidDailyRRULE(recurrence) ||
    isValidWeeklyRRULE(recurrence) ||
    isValidMonthlyRRULE(recurrence) ||
    isValidYearlyRRULE(recurrence)
  );
};

// ============================================================
// RDATE builder — used when RRULE can't be safely generated
// but a recurrence still needs to stay ONE series
// ============================================================

const buildRDATERecurrence = (event) => {
  const recurrence = event?.recurrenceId;
  const dates = recurrence?.datesSelected || [];

  if (dates.length === 0) return null;

  const sorted = [...new Set(dates.map((d) => new Date(d).getTime()))]
    .sort((a, b) => a - b)
    .map((t) => new Date(t));

  const duration =
    new Date(event.end).getTime() - new Date(event.start).getTime();

  const first = sorted[0];
  const remaining = sorted.slice(1);

  const plan = {
    mode: "rdate",
    start: first,
    end: new Date(first.getTime() + duration),
    recur: null,
    remainingDates: remaining, // 👈 NEW — raw dates for ICS local formatting
  };

  if (remaining.length > 0) {
    plan.recur = `RDATE:${remaining.map((d) => formatDate(d)).join(",")}`;
  }

  return plan;
};

// ============================================================
// PLAN BUILDER — single event vs RRULE series vs RDATE series
// ============================================================

export const getGoogleCalendarPlan = (event) => {
  const recurrence = event?.recurrenceId;
  const duration =
    new Date(event.end).getTime() - new Date(event.start).getTime();

  // Not a recurring event → plain single event
  if (event?.eventType !== "recurrence" || !recurrence) {
    return { mode: "single", start: event?.start, end: event?.end };
  }

  // ---------- Try RRULE first ----------
  if (canBuildRRULE(recurrence)) {
    if (recurrence.period === "week") {
      const repeatEvery = recurrence.repeatEvery || 1;
      const days = recurrence.weekDaySelected;
      let dt = new Date(recurrence.start);
      while (!days.some((d) => dt.getDay() === getDayIndex(d))) {
        dt.setDate(dt.getDate() + 1);
      }
      return {
        mode: "rrule",
        start: dt,
        end: new Date(dt.getTime() + duration),
        recur: `RRULE:FREQ=WEEKLY;INTERVAL=${repeatEvery};BYDAY=${getBYDAY(
          days,
        )};UNTIL=${formatDate(recurrence.end)}`,
      };
    }

    if (recurrence.period === "month") {
      const repeatEvery = recurrence.repeatEvery || 1;
      const days = recurrence.monthDaySelected;
      const freqMap = { 1: 1, 2: 2, 3: 3, 4: 4, 5: -1 };

      const sortedDates = (recurrence.datesSelected || [])
        .map((d) => new Date(d))
        .sort((a, b) => a - b);
      const start =
        sortedDates.length > 0 ? sortedDates[0] : new Date(recurrence.start);

      return {
        mode: "rrule",
        start,
        end: new Date(start.getTime() + duration),
        recur: `RRULE:FREQ=MONTHLY;INTERVAL=${repeatEvery};BYDAY=${getBYDAY(
          days,
        )};BYSETPOS=${freqMap[recurrence.monthFrequency]};UNTIL=${formatDate(
          recurrence.end,
        )}`,
      };
    }

    if (isValidDailyRRULE(recurrence)) {
      const repeatEvery = recurrence.repeatEvery || 1;
      const start = new Date(recurrence.start);
      return {
        mode: "rrule",
        start,
        end: new Date(start.getTime() + duration),
        recur: `RRULE:FREQ=DAILY;INTERVAL=${repeatEvery};UNTIL=${formatDate(
          recurrence.end,
        )}`,
      };
    }

    if (recurrence.period === "year") {
      const repeatEvery = recurrence.repeatEvery || 1;
      const start = new Date(recurrence.start);
      return {
        mode: "rrule",
        start,
        end: new Date(start.getTime() + duration),
        recur: `RRULE:FREQ=YEARLY;INTERVAL=${repeatEvery};UNTIL=${formatDate(
          recurrence.end,
        )}`,
      };
    }
  }

  // ---------- RRULE not possible → fall back to RDATE (still ONE series) ----------
  const rdatePlan = buildRDATERecurrence(event);
  if (rdatePlan) return rdatePlan;

  // Nothing usable at all — last-resort plain single event
  return { mode: "single", start: event?.start, end: event?.end };
};

// ============================================================
// EVENT BODY BUILDER — always returns ONE event object
// ============================================================

const buildBaseEventFields = (event) => {
  const description =
    event?.location === "hybrid"
      ? `${event?.description || ""}\n\nJoin link: ${event?.url || ""}`
      : event?.description || "";

  return {
    summary: event?.title || "",
    description,
    location:
      event?.location === "virtual" ? event?.url || "" : event?.address || "",
  };
};

export const buildGoogleCalendarEventBody = (event) => {
  const plan = getGoogleCalendarPlan(event);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const base = buildBaseEventFields(event);

  const body = {
    ...base,
    start: { dateTime: new Date(plan.start).toISOString(), timeZone },
    end: { dateTime: new Date(plan.end).toISOString(), timeZone },
  };

  if ((plan.mode === "rrule" || plan.mode === "rdate") && plan.recur) {
    body.recurrence = [plan.recur];
  }

  return body;
};

// ============================================================
// API CALLS
// ============================================================

export const insertGoogleCalendarEvent = async (accessToken, eventBody) => {
  const res = await fetch(
    "https://www.googleapis.com/calendar/v3/calendars/primary/events",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(eventBody),
    },
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err?.error?.message || "Failed to add event");
    error.status = res.status;
    throw error;
  }

  return res.json();
};

// ============================================================
// OAUTH TOKEN HANDLING (race-safe, cached)
// ============================================================

let tokenClient = null;
let cachedAccessToken = null;
let tokenExpiryTime = 0;

const getTokenClient = () => {
  if (!tokenClient) {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: "https://www.googleapis.com/auth/calendar.events",
      callback: () => {}, // overridden fresh per request below
    });
  }
  return tokenClient;
};

const isTokenValid = () =>
  cachedAccessToken && Date.now() < tokenExpiryTime - 30000;

const requestFreshToken = (client) =>
  new Promise((resolve, reject) => {
    client.callback = (tokenResponse) => {
      if (!tokenResponse?.access_token) {
        reject(new Error("Google authorization failed."));
        return;
      }
      cachedAccessToken = tokenResponse.access_token;
      tokenExpiryTime = Date.now() + (tokenResponse.expires_in || 3600) * 1000;
      resolve(tokenResponse.access_token);
    };
    client.error_callback = (err) => {
      reject(new Error(err?.message || "Google authorization error."));
    };
    client.requestAccessToken({ prompt: "" });
  });

// ============================================================
// PUBLIC ENTRY POINT
// ============================================================

export const addEventToGoogleCalendar = async (
  event,
  { onSuccess, onError } = {},
) => {
  if (!window.google?.accounts?.oauth2) {
    onError?.(new Error("Google service not loaded yet. Please try again."));
    return;
  }

  const eventBody = buildGoogleCalendarEventBody(event);

  try {
    const client = getTokenClient();
    const accessToken = isTokenValid()
      ? cachedAccessToken
      : await requestFreshToken(client);

    const result = await insertGoogleCalendarEvent(accessToken, eventBody);
    onSuccess?.(result);
  } catch (err) {
    if (err?.status === 401) {
      cachedAccessToken = null;
      tokenExpiryTime = 0;
      try {
        const client = getTokenClient();
        const accessToken = await requestFreshToken(client);
        const result = await insertGoogleCalendarEvent(accessToken, eventBody);
        onSuccess?.(result);
        return;
      } catch (retryErr) {
        onError?.(retryErr);
        return;
      }
    }
    onError?.(err);
  }
};

// ============================================================
// ICS EXPORT — reuses the SAME plan (RRULE/RDATE) as Google Calendar
// ============================================================

function formatICSDescription(text = "") {
  return text.replace(/\r\n|\r|\n/g, "\\n").replace(/(.{1,75})/g, "$1\r\n "); // fold lines per ICS spec
}

const formatICSLocalDateTime = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds())
  );
};

function buildVTimeZone(timeZone) {
  switch (timeZone) {
    case "America/New_York":
      return [
        "BEGIN:VTIMEZONE",
        "TZID:America/New_York",
        "X-LIC-LOCATION:America/New_York",

        "BEGIN:DAYLIGHT",
        "TZOFFSETFROM:-0500",
        "TZOFFSETTO:-0400",
        "TZNAME:EDT",
        "DTSTART:19700308T020000",
        "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU",
        "END:DAYLIGHT",

        "BEGIN:STANDARD",
        "TZOFFSETFROM:-0400",
        "TZOFFSETTO:-0500",
        "TZNAME:EST",
        "DTSTART:19701101T020000",
        "RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU",
        "END:STANDARD",

        "END:VTIMEZONE",
      ];

    default:
      return [];
  }
}

export const buildICSContent = (event) => {
  const plan = getGoogleCalendarPlan(event);
  const base = buildBaseEventFields(event);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const uid = `${event?.recurrenceId?._id || event?._id || Date.now()}@yourapp.com`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//YourApp//Event Export//EN",
    "CALSCALE:GREGORIAN",
     ...buildVTimeZone(timeZone),
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `SUMMARY:${event?.title || ""}`,
    `DTSTART;TZID=${timeZone}:${formatICSLocalDateTime(plan.start)}`,
    `DTEND;TZID=${timeZone}:${formatICSLocalDateTime(plan.end)}`,
    `DESCRIPTION:${formatICSDescription(base.description)}`,
    `URL:${event?.url || ""}`,
    `LOCATION:${base.location}`,
  ];

  if (plan.mode === "rrule" && plan.recur) {
    lines.push(plan.recur);
  }

  if (plan.mode === "rdate" && plan.remainingDates?.length > 0) {
   
    const rdateLocal = plan.remainingDates
      .map((d) => formatICSLocalDateTime(d))
      .join(",");
    lines.push(`RDATE;TZID=${timeZone}:${rdateLocal}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");

  return lines.join("\r\n");
};

export const downloadICS = (content, title) => {
  const blob = new Blob([content], {
    type: "text/calendar;charset=utf-8",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${title || "event"}.ics`;
  link.click();
};

export const exportEventAsICS = (event) => {
  const content = buildICSContent(event);
  downloadICS(content, event?.title);
};
